import { ArrowRight, Banknote, BarChart3, Eye, RefreshCw, Repeat2, TrendingUp } from 'lucide-react';
import React from 'react';
import usePage from '@dashboard/hooks/usePage';
import getConversionRate from '@dashboard/services/getConversionRate';
import { hasOneTimePrice, hasRecurringPrice } from '@dashboard/services/getBillingModel';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Link from '@dashboard/components/Link';
import * as KPICard from '../components/KPICard';
import RecentPayments from '../components/RecentPayments';
import RecentSubscriptions from '../components/RecentSubscriptions';
import PageOverviewHeader from '../components/PageOverviewHeader';
import { KPITrend, moneyFormatter, TopReferrers } from '../components/PageCharts';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import getRecoveredTooltipText from '@dashboard/services/abandonment/getRecoveredTooltipText';
import formatRecoveredStats from '@dashboard/services/abandonment/formatRecoveredStats';
import Spinner from '@dashboard/components/Spinner';
import { usePageSummary } from '../components/PageSummaryRange';
import { DetailBaseContext } from '@dashboard/lib/detailLinks';

export default function CheckoutOverview() {
  const { page } = usePage();
  const { product } = page;
  const { seller } = useAuth();
  const { summary, isLoading: overviewLoading } = usePageSummary();
  const totals = summary?.totals;

  const conversionRate = getConversionRate(totals?.conversions, totals?.views);
  // Mixed pages (one-time + recurring) get the reworked overview: no Revenue card
  // (5 cards, EventOverview layout) and the two recent tables side by side.
  const isMixed = hasOneTimePrice(product) && hasRecurringPrice(product);

  return (
    <div>
      <PageOverviewHeader
        page={page}
        side={
          <>
            <KPICard.Container>
              <KPICard.Icon icon={TrendingUp} />
              <KPICard.Header>
                <KPICard.Title>Conversion rate</KPICard.Title>
                <KPICard.InfoTooltip>
                  Conversions (payments + subscriptions) ÷ page views. Counts orders you later
                  archived or canceled.
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {overviewLoading ? <Spinner size="small" /> : `${conversionRate}%`}
              </KPICard.Value>
            </KPICard.Container>

            <KPICard.Container>
              <KPICard.Icon icon={RefreshCw} />
              <KPICard.Header>
                <KPICard.Title>Recovered</KPICard.Title>
                <KPICard.InfoTooltip>
                  {getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {overviewLoading ? (
                  <Spinner size="small" />
                ) : (
                  formatRecoveredStats({
                    totalRecovered: summary?.recovered?.totalRecovered,
                    totalAbandoned: summary?.recovered?.totalAbandoned,
                    totalRecoveredRevenue: summary?.recovered?.totalRecoveredRevenue,
                    // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed
                    currency: product.currency,
                  })
                )}
              </KPICard.Value>
            </KPICard.Container>
            <TopReferrers />
          </>
        }
      >
        {hasOneTimePrice(product) && (
          <>
            {!isMixed && (
              <KPICard.Container>
                <KPICard.Icon icon={BarChart3} />
                <KPICard.Header>
                  <KPICard.Title>Revenue</KPICard.Title>
                  <KPICard.InfoTooltip>
                    Gross revenue from paid, pending, unpaid and refunded payments, before refunds.
                    Includes archived and canceled orders.
                  </KPICard.InfoTooltip>
                </KPICard.Header>
                <KPICard.Value>
                  {overviewLoading ? (
                    <Spinner size="small" />
                  ) : (
                    getPriceLabel({
                      amount: totals?.revenue ?? 0,
                      // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed
                      currency: product.currency,
                    })
                  )}
                </KPICard.Value>
                <KPITrend metric="revenue" label="Revenue" formatValue={moneyFormatter(page)} />
              </KPICard.Container>
            )}

            <KPICard.Container>
              <KPICard.Icon icon={Banknote} />
              <KPICard.Header>
                <KPICard.Title>Payments</KPICard.Title>
                <KPICard.InfoTooltip>
                  {isMixed
                    ? 'Number of paid, partially paid, pending, unpaid and refunded payments, and their total before refunds. Not including archived or canceled orders.'
                    : 'Number of paid, partially paid, pending, unpaid and refunded payments. Not including archived or canceled orders.'}
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {overviewLoading ? (
                  <Spinner size="small" />
                ) : (
                  <>
                    {totals?.payments ?? 0}
                    {isMixed && (
                      <>
                        {' / '}
                        {getPriceLabel({
                          amount: totals?.revenue ?? 0,
                          currency: product.currency,
                        })}
                      </>
                    )}
                  </>
                )}
              </KPICard.Value>
              <KPITrend metric="payments" label="Payments" />
            </KPICard.Container>
          </>
        )}

        {hasRecurringPrice(product) && (
          <>
            <KPICard.Container>
              <KPICard.Icon icon={Repeat2} />
              <KPICard.Header>
                <KPICard.Title>Subscriptions</KPICard.Title>
                <KPICard.InfoTooltip>
                  Subscriptions that started. Includes canceled and completed subscriptions.
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {overviewLoading ? <Spinner size="small" /> : (totals?.subscriptions ?? 0)}
              </KPICard.Value>
              <KPITrend metric="subscriptions" label="Subscriptions" />
            </KPICard.Container>
          </>
        )}

        <KPICard.Container>
          <KPICard.Icon icon={Eye} />
          <KPICard.Header>
            <KPICard.Title>Page views</KPICard.Title>
            <KPICard.InfoTooltip>Total number of checkout page views.</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {overviewLoading ? <Spinner size="small" /> : (totals?.views ?? 0)}
          </KPICard.Value>
          <KPITrend metric="views" label="Page views" />
        </KPICard.Container>
      </PageOverviewHeader>

      {isMixed ? (
        <div className="grid grid-cols-2 gap-6">
          <RecentActivitySection title="Recent payments" href={`/pages/${page._id}/payments`}>
            <RecentPayments />
          </RecentActivitySection>
          <RecentActivitySection
            title="Recent subscriptions"
            href={`/pages/${page._id}/subscriptions`}
          >
            <RecentSubscriptions />
          </RecentActivitySection>
        </div>
      ) : (
        <>
          {hasOneTimePrice(product) && (
            <RecentActivitySection title="Recent payments" href={`/pages/${page._id}/payments`}>
              <RecentPayments />
            </RecentActivitySection>
          )}

          {hasRecurringPrice(product) && (
            <RecentActivitySection
              title="Recent subscriptions"
              href={`/pages/${page._id}/subscriptions`}
            >
              <RecentSubscriptions />
            </RecentActivitySection>
          )}
        </>
      )}
    </div>
  );
}

// One "Recent <type>" block (heading + View all link + list). Rendered per billing
// type so a mixed page shows both payments and subscriptions.
function RecentActivitySection({ title, href, children }) {
  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <CardHeaderTitle variant="root">{title}</CardHeaderTitle>

        <div>
          <Link href={href} className="flex items-center gap-x-1 whitespace-nowrap text-sm">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Rows open inside the page, under the screen this block's View all goes to. */}
      <DetailBaseContext.Provider value={href}>{children}</DetailBaseContext.Provider>
    </div>
  );
}
